import { useCallback, useMemo } from 'react';
import { useT } from '../i18n';
import { FilterSelect, FilterReset } from './FilterSelect';
import { usePageFilters } from '../context/PageFiltersContext';
import { projectDisplayCode } from '../utils/identifiers';
import { parseJSON } from './ProjectForm';
import type { Project } from '../types';

// The project / person / sponsor / collaborator filters shared by Organisation tâches
// and Planification des tâches. The selection lives in PageFiltersContext, so it
// stays the same when switching between the two pages (and across a reload).

const norm = (s: string | null | undefined) => (s ?? '').trim().toLowerCase();

/** Does the project pass the project / sponsor / collaborator filters? */
export function useProjectFilter() {
  const { taskFilterProjects, taskFilterSponsors, taskFilterCollabs } = usePageFilters();
  return useCallback((p: Project | undefined, id: number): boolean => {
    if (taskFilterProjects.length && !taskFilterProjects.includes(id)) return false;
    if (taskFilterSponsors.length) {
      const s = p ? parseJSON<any>(p.ownership, {}).sponsor : null;
      if (!s || !taskFilterSponsors.includes(s)) return false;
    }
    if (taskFilterCollabs.length) {
      const cs = p ? (parseJSON<any>(p.resources_effort, {}).collaborators || []) : [];
      if (!cs.some((c: any) => c?.name && taskFilterCollabs.includes(c.name))) return false;
    }
    return true;
  }, [taskFilterProjects, taskFilterSponsors, taskFilterCollabs]);
}

/** Is one of the given names (assignee, collaborators) among the selected persons? No selection = everyone. */
export function usePersonFilter() {
  const { taskFilterPersons } = usePageFilters();
  return useMemo(() => {
    const wanted = new Set(taskFilterPersons.map(norm));
    return (names: (string | null | undefined)[]) => wanted.size === 0 || names.some(n => !!n && wanted.has(norm(n)));
  }, [taskFilterPersons]);
}

/** The four filters (FilterSelect), plus "Réinitialiser" when any is set. `people` = names that appear on tasks. */
export function TaskFilters({ projects, people, children }: { projects: Project[]; people: string[]; children?: React.ReactNode }) {
  const { t } = useT();
  const f = usePageFilters();
  const projectOptions = useMemo(() => projects.map(p => ({ value: p.id, label: `${projectDisplayCode(p).toUpperCase()} — ${p.name}` })), [projects]);
  const personOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    people.forEach(n => { const name = n?.trim(); if (name && !byKey.has(name.toLowerCase())) byKey.set(name.toLowerCase(), name); });
    return [...byKey.values()].sort((a, b) => a.localeCompare(b)).map(s => ({ value: s, label: s }));
  }, [people]);
  const sponsorOptions = useMemo(() => {
    const set = new Set<string>();
    projects.forEach(p => { const s = parseJSON<any>(p.ownership, {}).sponsor; if (s) set.add(s); });
    return [...set].sort().map(s => ({ value: s, label: s }));
  }, [projects]);
  const collabOptions = useMemo(() => {
    const set = new Set<string>();
    projects.forEach(p => (parseJSON<any>(p.resources_effort, {}).collaborators || []).forEach((c: any) => { if (c?.name) set.add(c.name); }));
    return [...set].sort().map(s => ({ value: s, label: s }));
  }, [projects]);

  const anyActive = f.taskFilterProjects.length + f.taskFilterPersons.length + f.taskFilterSponsors.length + f.taskFilterCollabs.length > 0;
  const resetAll = () => { f.setTaskFilterProjects([]); f.setTaskFilterPersons([]); f.setTaskFilterSponsors([]); f.setTaskFilterCollabs([]); };

  return (
    <>
      <FilterSelect label={t('tasks.filterProjects')} options={projectOptions} selected={f.taskFilterProjects} onChange={f.setTaskFilterProjects} emptyLabel={t('filters.all')} width={280} />
      <FilterSelect label={t('tasks.filterPerson')} options={personOptions} selected={f.taskFilterPersons} onChange={f.setTaskFilterPersons} emptyLabel={t('tasks.everyone')} />
      <FilterSelect label={t('tasks.filterSponsor')} options={sponsorOptions} selected={f.taskFilterSponsors} onChange={f.setTaskFilterSponsors} emptyLabel={t('filters.all')} />
      <FilterSelect label={t('tasks.filterCollaborators')} options={collabOptions} selected={f.taskFilterCollabs} onChange={f.setTaskFilterCollabs} emptyLabel={t('filters.all')} />
      {children}
      {anyActive && <FilterReset onClick={resetAll} />}
    </>
  );
}
